import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { WorkflowResponse, WorkflowStatus } from '@/modules/workflows/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { StatusPill, getStatusVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

/**
 * Bits compartilhados entre SkillCard (view "Cards") e SkillListItem (view "Lista").
 * Skill = workflow com usageType='skill'. Não tem foto → avatar slate fixo com ícone.
 */

export interface SkillMeta {
  description: string;
  executionMode: 'sync' | 'async';
  nodeCount: number;
  baseLabel: string;
}

/** Rótulos pt-BR dos tipos de base consultável (espelha DB_TYPE_OPTIONS do QueryDatabaseConfig). */
const DB_TYPE_LABELS: Record<string, string> = {
  properties: 'Imóveis',
  vehicles: 'Veículos',
  products: 'Produtos',
  services: 'Serviços',
  documents: 'Documentos',
};

/**
 * Deriva descrição, modo de execução, contagem de nós e base consultada a partir dos
 * nodes da definition do workflow (mesma lógica da SkillsPage original).
 */
export function getSkillMeta(skill: WorkflowResponse): SkillMeta {
  const skillInput = skill.definition?.nodes?.find((n) => n.type === 'skill_input');
  const description =
    (skillInput?.data?.config as { description?: string } | undefined)?.description ||
    skill.description ||
    '';
  const executionMode =
    (skillInput?.data?.config as { executionMode?: 'sync' | 'async' } | undefined)
      ?.executionMode || 'sync';
  const nodeCount = skill.definition?.nodes?.length || 0;
  // Base consultada: derivada do 1º node action_query_database (databaseType → rótulo pt-BR)
  const queryNode = skill.definition?.nodes?.find((n) => n.type === 'action_query_database');
  const databaseType = (queryNode?.data?.config as { databaseType?: string } | undefined)
    ?.databaseType;
  const baseLabel = databaseType ? (DB_TYPE_LABELS[databaseType] ?? 'Base') : '';
  return { description, executionMode, nodeCount, baseLabel };
}

/** Labels pt-BR por status — cor vem do mapa semântico central (StatusPill, F62). */
export const SKILL_STATUS_LABELS: Record<WorkflowStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  inactive: 'Pausado',
  archived: 'Arquivado',
};

export const SkillStatusBadge: React.FC<{ status: WorkflowStatus; 'data-testid'?: string }> = ({
  status,
  'data-testid': dataTestId,
}) => (
  <StatusPill
    variant={getStatusVariant(status)}
    label={SKILL_STATUS_LABELS[status] ?? status}
    data-testid={dataTestId}
  />
);

/** Avatar do skill: slate #64748b fixo (via AvatarFallback) com ícone de raio. */
export const SkillAvatar: React.FC<{ size?: 'sm' | 'md' }> = ({ size = 'md' }) => (
  <Avatar className={size === 'sm' ? 'w-9 h-9' : 'w-11 h-11'} isSquircle>
    <AvatarFallback isSquircle>
      <Icon name="zap" className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} />
    </AvatarFallback>
  </Avatar>
);

/**
 * Chip da base de dados consultada pelo skill (mockup: trBadge — 1º badge do card).
 * Boxed pill; "Base: X" quando há node action_query_database, senão "Sem base".
 */
export const SkillBaseChip: React.FC<{ label: string }> = ({ label }) => (
  <span
    className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md text-xs font-semibold"
    style={{ backgroundColor: 'var(--theme-page-background)', color: 'var(--theme-card-text)' }}
  >
    <Icon name={label ? 'database' : 'zap'} className="w-3 h-3" />
    {label ? `Base: ${label}` : 'Sem base'}
  </span>
);

/** Chip de modo de execução (síncrono / assíncrono). */
export const SkillModeChip: React.FC<{ mode: 'sync' | 'async' }> = ({ mode }) => (
  <span
    className="inline-flex items-center gap-1 text-xs font-medium"
    style={{ color: 'var(--theme-muted-text)' }}
  >
    <Icon name="zap" className="w-3 h-3" />
    {mode === 'sync' ? 'Síncrono' : 'Assíncrono'}
  </span>
);

/** Chip de nº de etapas (nós) do skill. */
export const SkillNodesChip: React.FC<{ count: number }> = ({ count }) => (
  <span
    className="inline-flex items-center gap-1 text-xs font-medium"
    style={{ color: 'var(--theme-muted-text)' }}
  >
    <Icon name="activity" className="w-3 h-3" />
    {count} {count === 1 ? 'etapa' : 'etapas'}
  </span>
);

export function formatSkillDate(dateString?: string): string {
  if (!dateString) return 'nunca';
  try {
    // mockup: tempo RELATIVO ("há 2 min")
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ptBR });
  } catch {
    return 'Data inválida';
  }
}
